import { EMPTY } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { MobileQuestsService } from '@sifca-monorepo/clients';
import { QuestAnalyticsUpdateInput } from '@sifca-monorepo/widget-generator';
import { identity, omit, pickBy } from 'lodash';

type QuestAnalyticsSignal = 'viewed' | 'hovered' | 'clicked';

const QUEST_ANALYTICS_SIGNAL_PRIORITY: Record<QuestAnalyticsSignal, number> = {
  viewed: 0,
  hovered: 1,
  clicked: 2,
};

@Injectable({
  providedIn: 'root',
})
export class QuestAnalyticsService {
  private highestSignalByKey = new Map<string, QuestAnalyticsSignal>();

  constructor(private questsService: MobileQuestsService) {}

  trackImpressionViewed(questId: string): boolean {
    return this.trackSignal(questId, 'viewed');
  }

  trackHover(questId: string): boolean {
    return this.trackSignal(questId, 'hovered');
  }

  trackClick(questId: string): boolean {
    return this.trackSignal(questId, 'clicked');
  }

  private trackSignal(questId: string, signal: QuestAnalyticsSignal): boolean {
    const key = this.buildAnalyticsKey(questId);
    if (!key || !this.shouldTrack(key, signal)) {
      return false;
    }

    this.highestSignalByKey.set(key, signal);
    this.callApi(questId, signal);
    return true;
  }

  private shouldTrack(key: string, signal: QuestAnalyticsSignal): boolean {
    const currentSignal = this.highestSignalByKey.get(key);
    if (!currentSignal) {
      return true;
    }

    return QUEST_ANALYTICS_SIGNAL_PRIORITY[signal] > QUEST_ANALYTICS_SIGNAL_PRIORITY[currentSignal];
  }

  private buildAnalyticsKey(questId: string): string | null {
    if (!questId) {
      return null;
    }

    const sessionId = this.getStorageValue('sessionId');
    const posId = this.getStorageValue('posId');
    if (!sessionId || !posId) {
      return null;
    }

    return `${questId}|${sessionId}|${posId}`;
  }

  private getStorageValue(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private callApi(questId: string, signal: QuestAnalyticsSignal): void {
    let input;
    let geoData;
    geoData = JSON.parse(localStorage.getItem('geoData'));
    geoData = pickBy(geoData, identity);
    input = {
      ...(geoData ? { geoData: { ...omit(geoData, 'status') } } : {}),
      ...this.buildMutationInput(questId, signal),
    };
    try {
      this.questsService
        .upsertQuestAnalytics(input as QuestAnalyticsUpdateInput)
        .pipe(
          take(1),
          catchError(() => EMPTY),
        )
        .subscribe();
    } catch {}
  }

  private buildMutationInput(
    questId: string,
    signal: QuestAnalyticsSignal,
  ): Pick<QuestAnalyticsUpdateInput, 'quest' | 'impressionViewed' | 'hover' | 'click'> {
    switch (signal) {
      case 'clicked':
        return {
          quest: questId,
          impressionViewed: true,
          hover: true,
          click: true,
        };
      case 'hovered':
        return {
          quest: questId,
          impressionViewed: true,
          hover: true,
          click: false,
        };
      default:
        return {
          quest: questId,
          impressionViewed: true,
          hover: false,
          click: false,
        };
    }
  }
}
